import React from 'react';
import {Input, Form} from 'antd';
import './style.css';

/**
 * A customizable input component with integrated validation, styled using Ant Design.
 *
 * @param {Object} props - The properties passed to the input component.
 * @param {string} props.label - The label text for the input field.
 * @param {string} props.value - The current value of the input field.
 * @param {string} props.placeholder - Placeholder text to guide the user.
 * @param {string} props.type - The type of input (e.g., "text", "password", "email").
 * @param {string} props.labelPosition - Specifies the position of the label. Accepts "up" (above the input) or "aside" (beside the input).
 * @param {boolean} [props.required] - Indicates if the field is required. Defaults to `false`.
 * @param {(value: string) => void} [props.onChange] - Optional callback function triggered when the input value changes.
 *
 * @returns {React.ReactElement} A styled input component with validation and label positioning.
 */
type InputProps = {
    label: string;
    value: string;
    placeholder: string;
    type: string;
    labelPosition: string;
    required?: boolean;
    onChange?: (value: string) => void;
};

const InputComponent = ({label, value, type, placeholder, labelPosition, required, onChange}: InputProps) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) onChange(event.target.value);
    };

    return (
        <Form.Item
            label={labelPosition === 'aside' && label} className={'customLabel'}
            validateStatus={required && value === '' ? 'error' : undefined}
            help={required && value === '' ? 'This field is required' : undefined}
        >
            {labelPosition === 'up' && <label>{label}</label>}
            <Input type={type} value={value} onChange={handleChange} placeholder={placeholder} required={required}/>
        </Form.Item>
    );
};

export default InputComponent;